import type { SortDescriptor } from '@react-types/shared';
import type { UserListItem } from '@/types';

import React from 'react';

import { createSortDescriptor } from '@/components/table';

export function useUsersSorting(users: UserListItem[]) {
  const [sortDescriptor, setSortDescriptor] = React.useState<SortDescriptor | undefined>(undefined);

  const sortedUsers = React.useMemo(() => {
    if (!sortDescriptor?.column) return users;

    const copy = [...users];
    const { column, direction } = sortDescriptor;

    const compareStrings = (a: string, b: string) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' });

    copy.sort((a, b) => {
      let cmp = 0;

      switch (column) {
        case 'name': {
          const aName = `${a.first_name} ${a.last_name}`.trim() || a.email;
          const bName = `${b.first_name} ${b.last_name}`.trim() || b.email;

          cmp = compareStrings(aName, bName);
          break;
        }
        case 'verified': {
          const aV = Number(Boolean(a.email_verified_at));
          const bV = Number(Boolean(b.email_verified_at));

          cmp = aV - bV;
          break;
        }
        case 'organizations': {
          const aOrgCount = a.organizations?.length ?? 0;
          const bOrgCount = b.organizations?.length ?? 0;

          if (aOrgCount !== bOrgCount) {
            cmp = aOrgCount - bOrgCount;
          } else if (aOrgCount > 0 && bOrgCount > 0) {
            // If same count, sort by first organization name
            const aOrgName = a.organizations[0]?.name ?? '';
            const bOrgName = b.organizations[0]?.name ?? '';
            cmp = compareStrings(aOrgName, bOrgName);
          }
          break;
        }
        case 'roles': {
          const aRoleCount = a.roles?.length ?? 0;
          const bRoleCount = b.roles?.length ?? 0;

          if (aRoleCount !== bRoleCount) {
            cmp = aRoleCount - bRoleCount;
          } else if (aRoleCount > 0 && bRoleCount > 0) {
            // If same count, sort by first role name
            const aRoleName = a.roles[0]?.name ?? '';
            const bRoleName = b.roles[0]?.name ?? '';
            cmp = compareStrings(aRoleName, bRoleName);
          }
          break;
        }
        case 'created_at': {
          const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
          cmp = aDate - bDate;
          break;
        }
        default:
          cmp = 0;
      }

      return direction === 'descending' ? -cmp : cmp;
    });

    return copy;
  }, [users, sortDescriptor]);

  return {
    sortDescriptor,
    setSortDescriptor,
    sortedUsers
  };
}

